import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, X, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API_URL, STATUS_LABELS, PROGRAM_LABELS, DOC_LEVEL_LABELS, fmtPrice, boatTitle, imgUrl, toggleCompare, getCompare } from '../lib/boats';

const annual = (b) => { const bd = b.budget || {}; const t = ['port_annual', 'insurance_annual', 'maintenance_annual', 'fuel_annual'].reduce((s, k) => s + (Number(bd[k]) || 0), 0); return t || null; };
const ROWS = [
  ['Prix', (b) => fmtPrice(b.price)], ['Année', (b) => b.year], ['Longueur', (b) => b.length && `${b.length} m`], ['Largeur', (b) => b.beam && `${b.beam} m`], ["Tirant d'eau", (b) => b.draft && `${b.draft} m`],
  ['Cabines', (b) => b.cabins], ['Couchages', (b) => b.berths], ['Motorisation', (b) => [b.engine_type, b.engine_brand, b.engine_power].filter(Boolean).join(' ') || null], ['Heures moteur', (b) => b.engine_hours != null && `${b.engine_hours} h`],
  ['Localisation', (b) => b.location || b.port], ['Budget annuel indicatif', (b) => fmtPrice(annual(b))], ['Niveau de documentation', (b) => DOC_LEVEL_LABELS[b.doc_level]],
  ['Travaux connus', (b) => (b.works_todo?.length ? b.works_todo.join(' · ') : b.works_todo ? 'Aucun renseigné' : null)], ['Programme recommandé', (b) => b.programs?.map((p) => PROGRAM_LABELS[p]).join(' · ') || null],
  ['Points forts', (b) => b.highlights?.join(' · ') || null], ['Points à vérifier', (b) => b.checkpoints?.join(' · ') || null],
];

export default function BoatsCompare() {
  const [params] = useSearchParams();
  const [boats, setBoats] = useState(null);
  const slugsParam = params.get('slugs') || getCompare().join(',');
  const slugs = slugsParam.split(',').filter(Boolean).slice(0, 3);

  useEffect(() => {
    const list = slugsParam.split(',').filter(Boolean).slice(0, 3);
    if (!list.length) { setBoats([]); return; }
    fetch(`${API_URL}/api/boats-compare?slugs=${list.join(',')}`).then((r) => r.json()).then((d) => setBoats(d.boats || [])).catch(() => setBoats([]));
  }, [slugsParam]);

  const remove = (slug) => { toggleCompare(slug); const rest = slugs.filter((s) => s !== slug); window.location.assign(rest.length ? `/bateaux-a-vendre/comparer?slugs=${rest.join(',')}` : '/bateaux-a-vendre'); };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Helmet><title>Comparateur de bateaux | ValidatedBoat</title><meta name="robots" content="noindex, follow" /></Helmet>
      <Header />
      <main className="max-w-[87.5rem] mx-auto px-5 md:px-10 pt-28 md:pt-32 pb-24">
        <Link to="/bateaux-a-vendre" className="inline-flex items-center gap-1 text-sm text-[#888680] hover:text-[#d9fb06] mb-6"><ArrowLeft size={14} /> Retour aux bateaux</Link>
        <h1 className="font-black text-3xl sm:text-4xl lg:text-5xl text-[#d9fb06] uppercase mb-2">Comparer les bateaux</h1>
        <p className="text-[#888680] mb-8">Les différences sont mises en évidence pour vous aider à lire. Aucun bateau n'est déclaré « meilleur » : le bon choix dépend de votre projet.</p>
        {boats === null ? <p className="text-[#888680]"><Loader2 className="animate-spin inline mr-2" /> Chargement…</p> : boats.length === 0 ? (
          <div className="bg-[#302f2c] border border-[#3f4816] rounded-2xl p-10 text-center" data-testid="compare-empty"><p className="text-[#dfddd6] mb-4">Aucun bateau à comparer. Ajoutez jusqu'à 3 bateaux depuis le catalogue avec le bouton « Comparer ».</p><Link to="/bateaux-a-vendre" className="bg-[#d9fb06] text-[#1a1c1b] rounded-full px-6 py-3 font-bold uppercase text-sm">Voir les bateaux</Link></div>
        ) : (
          <div className="overflow-x-auto -mx-5 px-5 md:mx-0 md:px-0" data-testid="compare-table">
            <table className="w-full min-w-[640px] text-sm border-separate border-spacing-0">
              <thead><tr>
                <th className="sticky left-0 bg-[#1a1c1b] text-left text-xs text-[#888680] uppercase p-3 w-44">Critère</th>
                {boats.map((b) => (
                  <th key={b.slug} className="p-3 text-left align-top bg-[#302f2c] border border-[#3f4816] rounded-t-xl" data-testid={`compare-col-${b.slug}`}>
                    <div className="relative">
                      <button type="button" onClick={() => remove(b.slug)} aria-label={`Retirer ${boatTitle(b)}`} className="absolute -top-1 -right-1 p-1 text-[#888680] hover:text-[#d9fb06]" data-testid={`compare-remove-${b.slug}`}><X size={16} /></button>
                      <Link to={`/bateaux-a-vendre/${b.slug}`}>{b.photos?.[0] ? <img src={imgUrl(b.photos[0].thumb_url)} alt="" className="w-full aspect-[4/3] object-cover rounded-lg mb-2" /> : <div className="w-full aspect-[4/3] rounded-lg bg-[#1a1c1b] mb-2" />}<p className="font-bold text-[#dfddd6]">{boatTitle(b)}</p></Link>
                      <p className="text-xs text-[#888680]">{STATUS_LABELS[b.status]}</p>
                    </div>
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {ROWS.map(([label, fn]) => {
                  const vals = boats.map((b) => fn(b) ?? null);
                  if (vals.every((v) => v == null)) return null;
                  const differs = new Set(vals.map((v) => String(v))).size > 1;
                  return (
                    <tr key={label} data-testid={`compare-row-${label}`}>
                      <th scope="row" className="sticky left-0 bg-[#1a1c1b] text-left text-xs text-[#888680] font-bold uppercase p-3 border-t border-[#3f4816]">{label}{differs && <span className="ml-1 text-[#d9fb06]" title="Différence entre les bateaux">●</span>}</th>
                      {vals.map((v, i) => <td key={i} className={`p-3 border-t border-l border-[#3f4816] text-[#dfddd6] ${differs ? 'bg-[#d9fb06]/5' : ''}`}>{v ?? <span className="text-[#888680]">Non renseigné</span>}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
